import type { RecurringPayment, RecurringPaymentCadence } from '@/lib/api';

const CADENCE_LABEL: Record<RecurringPaymentCadence, string> = {
  Unknown: 'Recorrente',
  Weekly: 'Semanal',
  Monthly: 'Mensal',
  Yearly: 'Anual',
};

const CADENCE_SUFFIX: Record<RecurringPaymentCadence, string> = {
  Unknown: '/mês',
  Weekly: '/semana',
  Monthly: '/mês',
  Yearly: '/ano',
};

const WEEKS_PER_MONTH = 52 / 12;

export function cadenceLabel(cadence: RecurringPaymentCadence | string): string {
  return CADENCE_LABEL[cadence as RecurringPaymentCadence] ?? 'Recorrente';
}

export function cadenceSuffix(cadence: RecurringPaymentCadence | string): string {
  return CADENCE_SUFFIX[cadence as RecurringPaymentCadence] ?? '/mês';
}

/** Amount normalized to one month: yearly ÷ 12, weekly × 52/12, else as-is. */
export function monthlyEquivalent(amount: number, cadence: RecurringPaymentCadence | string): number {
  switch (cadence) {
    case 'Yearly':
      return amount / 12;
    case 'Weekly':
      return amount * WEEKS_PER_MONTH;
    default:
      return amount;
  }
}

export interface CadenceTotals {
  /** Sum of non-yearly amounts, weekly normalized to a month (R$/mês). */
  monthlyTotal: number;
  /** Sum of yearly amounts, per year (R$/ano). */
  yearlyTotal: number;
  /** monthlyTotal + yearlyTotal ÷ 12 — the true monthly cost. */
  monthlyEquivalentTotal: number;
}

export function cadenceTotals(items: Pick<RecurringPayment, 'amount' | 'cadence'>[]): CadenceTotals {
  let monthlyTotal = 0;
  let yearlyTotal = 0;
  for (const item of items) {
    const amount = Math.abs(item.amount);
    if (item.cadence === 'Yearly') {
      yearlyTotal += amount;
    } else {
      monthlyTotal += monthlyEquivalent(amount, item.cadence);
    }
  }
  return { monthlyTotal, yearlyTotal, monthlyEquivalentTotal: monthlyTotal + yearlyTotal / 12 };
}
